import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Compass, CheckCircle } from 'lucide-react';

/** The baseline gate. Module 2 presents this before any module content. */
export const BaselineGate = ({ gate, canBypass, onBypass }) => (
  // Lit glass over the dark theme — the same treatment as the Participants tiles.
  <Card
    className="border border-cyan-300/25 bg-white/5 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.35)]"
    data-testid="baseline-gate"
  >
    <CardContent className="p-8 text-center">
      <Compass className="w-10 h-10 text-cyan-300 mx-auto mb-4" />
      <h2 className="text-base md:text-lg font-semibold text-white mb-3">
        {gate.status === 'in_progress' ? 'Continue your baseline assessment' : gate.label}
      </h2>
      <p className="text-sm md:text-base text-gray-300 max-w-xl mx-auto whitespace-pre-line">
        {gate.instructions
          || 'This is the base everything else in the program is measured against. It opens before the module content.'}
      </p>
      <p className="text-xs text-white/45 mt-3">
        {gate.question_count} questions, one at a time. Your progress saves as you go.
        {gate.status === 'in_progress' ? ` ${gate.answered_count} answered so far.` : ''}
      </p>
      <Link to={`/dashboard/assessments/${gate.assessment_id}/take`}>
        <Button
          className="mt-6 bg-gradient-to-r from-cyan-500 to-purple-500 text-white"
          size="lg"
          data-testid="baseline-gate-start-btn"
        >
          {gate.status === 'in_progress' ? 'Resume assessment' : 'Begin assessment'}
        </Button>
      </Link>
      {canBypass && (
        <div className="mt-6 pt-4 border-t border-white/10">
          <button
            onClick={onBypass}
            className="text-xs text-white/45 underline transition-colors duration-200 hover:text-white/80"
            data-testid="baseline-gate-admin-bypass"
          >
            View the module content without taking it
          </button>
          <p className="text-[11px] text-white/30 mt-2">
            For admins and practitioners, so you can reach the sections you look after.
          </p>
        </div>
      )}
    </CardContent>
  </Card>
);

/** Shown on the final folder: submitting this assessment is the module completion. */
export const EndOfModuleGate = ({ gate, onStart, starting }) => {
  if (gate.status === 'completed') {
    return (
      <div className="pt-4 border-t border-white/10" data-testid="end-assessment-completed">
        <Link to={`/dashboard/assessments/results/${gate.attempt_id}`}>
          <Button
            variant="outline"
            className="w-full border-white/20 bg-white/5 text-white transition-colors duration-200 hover:bg-white/15"
            size="lg"
            data-testid="end-assessment-result-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2 text-emerald-400" />
            {gate.label} completed — {gate.overall_score}%, {gate.overall_stage_name}. View results
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-4 border-t border-white/10 space-y-2" data-testid="end-assessment-gate">
      <Button
        onClick={onStart}
        disabled={starting}
        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white"
        size="lg"
        data-testid="end-assessment-start-btn"
      >
        {gate.status === 'in_progress' ? 'Resume' : 'Begin'} {gate.label}
      </Button>
      <p className="text-center text-xs text-white/45">
        Submitting this assessment completes the module and unlocks the next one.
      </p>
    </div>
  );
};
